// Standee id rules and the catalog-driven id mapping (docs/three-standee-scale-spec.md, 5.2).
// Pure and erasable: no three, React or DOM imports.

export const STANDEE_ID = /^[a-z][a-z0-9-]{0,55}$/;

/** Lowercase; each run of characters outside [a-z0-9] becomes one '-'; trim '-' at both ends. */
export function normalizeKey(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
/** normalizeKey(raw) with the prefix prepended. Null when the result is not a standee id. */
export function standeeIdFor(raw: string, prefix = ''): string | null {
  const id = prefix + normalizeKey(raw);
  return STANDEE_ID.test(id) ? id : null;
}

export interface StandeeSubject { catalogKey: string | null; fields: Record<string, unknown>; catalog: { fields?: Record<string, unknown> } | null }
export type StandeeIdResult = { id: string } | { id: null; reason: 'no-catalog' | 'no-field' | 'invalid' };

export function resolveStandeeId(
  style: { id?: string; idFrom?: 'catalog' | 'field'; field?: string; prefix?: string },
  subject: StandeeSubject | null | undefined,
): StandeeIdResult {
  if (style.id !== undefined) return { id: style.id };
  if (style.idFrom === 'catalog') {
    if (!subject || subject.catalogKey === null) return { id: null, reason: 'no-catalog' };
    const id = standeeIdFor(subject.catalogKey, style.prefix);
    return id === null ? { id: null, reason: 'invalid' } : { id };
  }
  if (style.idFrom === 'field' && style.field !== undefined) {
    const v = subject?.fields?.[style.field] ?? subject?.catalog?.fields?.[style.field];
    let value: string;
    if (typeof v === 'string') value = v;
    else if (typeof v === 'number' && Number.isInteger(v)) value = String(v);
    else return { id: null, reason: 'no-field' };
    const id = standeeIdFor(value, style.prefix);
    return id === null ? { id: null, reason: 'invalid' } : { id };
  }
  return { id: null, reason: 'no-field' };
}
